import { RootEpic } from 'MyTypes';
import { intersection, isEmpty, omit } from 'lodash/fp';
import { empty, forkJoin, from, of } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    ignoreElements,
    map,
    mergeMap,
    switchMap,
    tap,
} from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { updateLocaleWith } from '@portal/common/locale';
import { DATA_PROJECT_ID_KEY, LOCATION_ID_KEY } from '@portal/common/models/data-key';
import { getMetricLabelOverridesByDataProject } from '@portal/common/utility/metric-label-overrides';

import { queryVolumeKeyGetter } from '../../scenes/InsightsScene/filters/VisualizationsFilter';
import { getDatasets } from '../data/selectors';
import {
    changeDataProjectId,
    changeInsightsFilters,
    getInsightsCompanyUsageData,
    getInsightsData,
    initializeInsightsFilters,
    setVisualizationOptions,
    validateAndSetInsightsFilters,
} from './actions';
import {
    BASE_VISUALIZATION_OPTIONS,
    ChartType,
    EXCLUDED_COMPANY_USAGE_DATA_PROJECTS,
    EventType,
    VisualizationType,
} from './models';
import {
    getInsightsCompanyUsageDataResponse,
    getInsightsDataProjectId,
    getInsightsEventType,
    getInsightsOrganizations,
    getInsightsSelectedFilters,
    getInsightsVisualizations,
} from './selectors';
import { generateAnalyticsAPIParams } from './utils';

const companyUsageAPIParams = {
    [VisualizationType.TopLocations]: {
        aggregation_function: 'count',
        aggregation_period: 'none',
        group_by: LOCATION_ID_KEY,
        unfold_array: LOCATION_ID_KEY,
    },
    [VisualizationType.QueryVolume]: {
        aggregation_function: 'count',
        aggregation_period: 'none',
    },
    [VisualizationType.TotalVisits]: {
        aggregation_function: 'count',
    },
    [VisualizationType.TotalQueries]: {
        aggregation_function: 'count',
    },
    [VisualizationType.EngagementTrends]: {
        aggregation_function: null,
        aggregation_period: null,
    },
    [VisualizationType.LicenseInformation]: {},
    [VisualizationType.QueriesByDataType]: {
        aggregation_function: 'count',
        aggregation_period: 'none',
        unfold_array: 'queried_data_types',
        group_by: 'queried_data_types',
    },
};

export const validateAndSetFilters: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf([changeInsightsFilters])),
        switchMap((action) => {
            const state = state$.value.insights;
            const { eventType } = state;
            const filters = state[eventType];
            const commonFilterKeys = ['toDate', 'fromDate', 'organizations'];
            const isCommonFilter = !isEmpty(
                intersection(Object.keys(action.payload), commonFilterKeys)
            );
            const commonFilters = isCommonFilter ? action.payload : {};
            const newState = {
                ...state,
                [eventType]: {
                    ...filters,
                    ...action.payload,
                },
                ...commonFilters,
            };

            if (filters.chartType === ChartType.table && !('dataTableParams' in action.payload)) {
                const { orderBy, orderDirection } = newState[eventType].dataTableParams;
                const sorting =
                    action.payload.groupBy === orderBy ||
                    !action.payload?.dataTableColumns?.includes(orderBy)
                        ? {}
                        : { orderBy, orderDirection };
                newState[eventType].dataTableParams = {
                    page: 1,
                    perPage: 10,
                    ...sorting,
                };
            }

            if (eventType === EventType.companyUsage) {
                if (isCommonFilter) {
                    newState[eventType].dataResponses = {};
                }

                if (action.payload.hasOwnProperty('timeUnit')) {
                    const visualizationsToClear = (
                        state[EventType.companyUsage].visualizations || []
                    ).filter(({ type, withTimeUnitFilter }) => {
                        return (
                            withTimeUnitFilter &&
                            state[EventType.companyUsage].timeUnit[type] !==
                                action.payload.timeUnit[type]
                        );
                    });
                    newState[eventType].dataResponses = omit(
                        visualizationsToClear.map(({ key }) => key),
                        state[EventType.companyUsage].dataResponses
                    );
                }
            }

            return of(validateAndSetInsightsFilters(newState));
        })
    );

export const loadInsightsData: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getInsightsData.request])),
        switchMap((action) => {
            const state = state$.value;
            const params = generateAnalyticsAPIParams(state);
            const { eventType } = state.insights;

            return from(api.analyticsReports[eventType](params)).pipe(
                map((res) => getInsightsData.success(res)),
                catchError((message: string) => of(getInsightsData.failure(message)))
            );
        })
    );
};

export const loadInsightsCompanyUsageData: RootEpic = (action$, state$, dependencies) => {
    const { api } = dependencies;
    return action$.pipe(
        filter(isActionOf([getInsightsCompanyUsageData.request])),
        switchMap((action) => {
            const state = state$.value;
            const eventType = getInsightsEventType(state);
            const { timeUnit } = getInsightsSelectedFilters(state);
            const dataProjectId = getInsightsDataProjectId(state);
            const params = generateAnalyticsAPIParams(state);
            let visualizations = getInsightsVisualizations(state);
            const dataByVisualizationKey = getInsightsCompanyUsageDataResponse(state);

            visualizations = visualizations.filter(({ key }) =>
                isEmpty(dataByVisualizationKey[key])
            );

            if (isEmpty(visualizations)) {
                return of(getInsightsCompanyUsageData.success({}));
            }

            const observables = visualizations.reduce((acc, config) => {
                const { type, key, dataType, filter, globalData, supportedDataProjectIds } = config;

                const apiRequest = api.analyticsReports[eventType][type];

                const relevantDataProjectIds = isEmpty(supportedDataProjectIds)
                    ? dataProjectId
                    : intersection(dataProjectId, supportedDataProjectIds);

                const sharedRequestParams = {
                    ...params,
                    ...companyUsageAPIParams[type],
                    ...(filter && {
                        group_by: filter,
                        unfold_array: filter,
                    }),
                    ...(dataType && { queried_data_types: dataType }),
                    ...(timeUnit[type] && { aggregation_period: timeUnit[type] }),
                };

                if (globalData) {
                    const params = omit([DATA_PROJECT_ID_KEY], sharedRequestParams);
                    acc[key] = from(apiRequest(params));
                    return acc;
                }

                acc[key] = forkJoin(
                    relevantDataProjectIds.reduce((all, dataProjectId) => {
                        all[dataProjectId] = from(
                            apiRequest({
                                ...sharedRequestParams,
                                data_project_id: dataProjectId,
                            })
                        );
                        return all;
                    }, {})
                );

                return acc;
            }, {});

            return forkJoin(observables).pipe(
                mergeMap((responsesByVizType) =>
                    of(
                        getInsightsCompanyUsageData.success(
                            Object.entries(responsesByVizType).reduce(
                                (acc, [dataType, response]) => {
                                    if (dataType === VisualizationType.LicenseInformation) {
                                        acc[dataType] = response;
                                    } else if (response.columns && response.records) {
                                        acc[dataType] = {
                                            records: response.records,
                                            columns: [...response.columns, DATA_PROJECT_ID_KEY],
                                        };
                                    } else {
                                        acc[dataType] = Object.entries(response).reduce(
                                            (
                                                mergedResponses,
                                                [dataProjectId, responseByDataProjectId],
                                                index
                                            ) => {
                                                if (index === 0) {
                                                    mergedResponses.columns = [
                                                        ...responseByDataProjectId.columns,
                                                        DATA_PROJECT_ID_KEY,
                                                    ];
                                                }
                                                mergedResponses.records.push(
                                                    ...responseByDataProjectId.records.map(
                                                        (record) => [...record, dataProjectId]
                                                    )
                                                );
                                                return mergedResponses;
                                            },
                                            { columns: [], records: [] }
                                        );
                                    }
                                    return acc;
                                },
                                {}
                            )
                        )
                    )
                ),
                catchError((error) => of(getInsightsCompanyUsageData.failure(error)))
            );
        })
    );
};

export const triggerLoadDataOnFiltersChange: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(
            isActionOf([
                validateAndSetInsightsFilters,
                initializeInsightsFilters,
                changeDataProjectId,
            ])
        ),
        switchMap((action) => {
            const state = state$.value;
            const eventType = getInsightsEventType(state);
            if (eventType === EventType.companyUsage) {
                const organizations = getInsightsOrganizations(state);
                const dataProjectId = getInsightsDataProjectId(state);
                const visualizations = getInsightsVisualizations(state);
                if (isEmpty(organizations) || isEmpty(visualizations) || isEmpty(dataProjectId)) {
                    return empty();
                }
                return of(getInsightsCompanyUsageData.request());
            }
            return of(getInsightsData.request());
        })
    );

export const updateVisualizationOptions: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(isActionOf(initializeInsightsFilters)),
        switchMap((action) => {
            const state = state$.value;
            const eventType = getInsightsEventType(state);

            if (eventType === EventType.companyUsage) {
                const datasets = getDatasets(state) || [];

                const queryVolumeOptions = datasets
                    .filter(
                        ({ primary_entity_id_filter, data_project_id }) =>
                            !!primary_entity_id_filter &&
                            !EXCLUDED_COMPANY_USAGE_DATA_PROJECTS.includes(data_project_id)
                    )
                    .reduce((acc, { data_type, primary_entity_id_filter, data_project_id }) => {
                        const key = `${data_type}_${primary_entity_id_filter}`;
                        if (!acc[key]) {
                            acc[key] = {
                                type: VisualizationType.QueryVolume,
                                supportedDataProjectIds: [data_project_id],
                                dataType: data_type,
                                filter: primary_entity_id_filter,
                            };
                        } else {
                            acc[key].supportedDataProjectIds.push(data_project_id);
                        }
                        return acc;
                    }, {});

                const visualizationOptions = Object.values(queryVolumeOptions).map((option) => ({
                    ...option,
                    key: queryVolumeKeyGetter(option),
                }));

                return of(
                    setVisualizationOptions([
                        ...BASE_VISUALIZATION_OPTIONS,
                        ...visualizationOptions,
                    ])
                );
            }
            return empty();
        })
    );

// Updates metric_2 ("Percent") label based on selected data project in Insights.
// When a single GBD or R&E data project is selected, uses project-specific label
// (e.g., "Percent of All-Cause" for GBD). Otherwise uses default "Percent".
// Watches state$ to handle both initial load and filter changes.
export const updateMetricLabelsForDataProject: RootEpic = (action$, state$) =>
    state$.pipe(
        map(getInsightsDataProjectId),
        distinctUntilChanged(),
        tap((dataProjectIds) => {
            // Use project-specific label only when a single data project is selected
            const singleSelectedDataProjectId =
                dataProjectIds?.length === 1 ? dataProjectIds[0] : undefined;
            updateLocaleWith(getMetricLabelOverridesByDataProject(singleSelectedDataProjectId));
        }),
        ignoreElements()
    );
